import React, { createContext, useCallback, useEffect, useState } from "react";
import { CardNetwork } from "types/cardNetwork";
import { local } from "utils/storage";

interface LocalConfigState {
  showLastFour: boolean;
  showCashflowConfigLink: boolean;
  cardNetwork: CardNetwork;
}

export interface LocalConfig extends LocalConfigState {
  toggleLastFour: () => void;
  toggleCashflowConfigLink: () => void;
  updateCardNetwork: (network: CardNetwork) => void;
}

const DEFAULT_CONFIG: LocalConfig = {
  showLastFour: true,
  showCashflowConfigLink: true,
  toggleLastFour: () => {},
  toggleCashflowConfigLink: () => {},
  updateCardNetwork: () => {},
  cardNetwork: CardNetwork.visa,
};

const storeConfig = (config: LocalConfigState) => {
  local.setItem("appConfig", config);
};

const LocalConfigContext = createContext<LocalConfig>({
  ...DEFAULT_CONFIG,
});

interface Props {
  children?: React.ReactNode;
}

export const LocalConfigContextProvider = ({ children }: Props) => {
  const [showLastFour, setShowLastFour] = useState(true);
  const [showCashflowConfigLink, setShowCashflowConfigLink] = useState(true);
  const [cardNetwork, setCardNetwork] = useState(CardNetwork.visa);

  const configState = {
    showLastFour,
    showCashflowConfigLink,
    cardNetwork,
  };

  const hydrateConfig = () => {
    const storedConfig = local.getItem("appConfig");
    if (storedConfig) {
      setShowLastFour(storedConfig.showLastFour);
      setShowCashflowConfigLink(storedConfig.showCashflowConfigLink);
    }
  };

  const toggleLastFour = useCallback(() => {
    setShowLastFour((prev) => {
      storeConfig({ ...configState, showLastFour: !prev });
      return !prev;
    });
  }, [configState]);

  const toggleCashflowConfigLink = useCallback(() => {
    setShowCashflowConfigLink((prev) => {
      storeConfig({ ...configState, showCashflowConfigLink: !prev });
      return !prev;
    });
  }, [configState]);

  const updateCardNetwork = useCallback((network: CardNetwork) => {
    setCardNetwork(() => {
      storeConfig({ ...configState, cardNetwork: network });
      return network;
    });
  }, []);

  const config = {
    ...configState,
    toggleLastFour,
    toggleCashflowConfigLink,
    updateCardNetwork,
  };

  useEffect(() => {
    hydrateConfig();
  }, []);

  return (
    <LocalConfigContext.Provider value={config}>
      {children}
    </LocalConfigContext.Provider>
  );
};

export default LocalConfigContext;
